import { useDeferredValue, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Check, CheckCircle2, ExternalLink, Link2, Loader2, MessageSquare, RefreshCw, Search, XCircle } from 'lucide-react';
import { useLocation } from 'wouter';

import { apiRequest } from '@/lib/queryClient';
import { showSuccessNotification } from '@/lib/notification';
import { useMartinaGlobal } from '@/contexts/ExAiGlobalContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { calendarInviteIssueTerminalContext } from './calendarInviteIssueTurnContext';
import {
  CALENDAR_REVIEW_LABELS,
  CALENDAR_REVIEW_ENTITY_LABELS,
  CALENDAR_REVIEW_OPEN_EVENT,
  type CalendarReviewEntityKind,
  type CalendarReviewCode,
} from '@shared/calendarReviewContract';

type CalendarInviteIssue = {
  messageId: number;
  accountEmail: string;
  subject: string | null;
  diagnostic: string | null;
  sourceHref: string;
  updatedAt?: string;
};

export type CalendarReviewItem = {
  id: string;
  entityType: 'meeting' | 'email_invite_issue';
  code: CalendarReviewCode;
  meetingId?: number | null;
  messageId?: number | null;
  title?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  location?: string | null;
  organizerEmail?: string | null;
  meetingType?: string | null;
  entityKind?: CalendarReviewEntityKind;
  source?: string | null;
  diagnostic?: string | null;
  accountEmail?: string | null;
  sourceHref: string;
};

export type CalendarInviteIssueResponse = {
  issues?: CalendarInviteIssue[];
  review?: {
    total: number;
    counts: {
      pending_confirmation: number;
      attendance_pending: number;
      parsing_error: number;
      interpretation_error: number;
    };
    items: CalendarReviewItem[];
  };
};

type ExistingMeetingCandidate = {
  id: number;
  title: string;
  startTime: string;
  endTime: string;
  location: string | null;
  meetingType: string;
  matchReasons: Array<'same_day' | 'similar_title' | 'search_match'>;
};

type ExistingMeetingCandidateResponse = {
  sourceSubject: string | null;
  candidates: ExistingMeetingCandidate[];
};

export const CALENDAR_REVIEW_QUERY_KEY = ['/api/email/promotion-buffer/calendar-invite-issues'] as const;

const issueLabels: Record<string, string> = {
  ics_bytes_unavailable: 'Allegato ICS senza byte verificabili',
  ics_parse_failed: 'Allegato ICS non interpretabile',
  text_invite_unresolved: 'Convocazione testuale non verificabile',
  text_time_unresolved: 'Orario dell’impegno da verificare',
  ics_programme_identity_ambiguous: 'Invito calendario con più partecipazioni da distinguere',
  provider_connectivity: 'Origine temporaneamente irraggiungibile',
};

function diagnosticLabel(diagnostic: string | null | undefined): string {
  if (!diagnostic) return 'Verifica calendario necessaria';
  return issueLabels[diagnostic] ?? diagnostic;
}

function candidateMatchLabel(reasons: ExistingMeetingCandidate['matchReasons']): string | null {
  if (reasons.includes('same_day') && reasons.includes('similar_title')) return 'Stesso giorno e titolo simile';
  if (reasons.includes('same_day')) return 'Stesso giorno';
  if (reasons.includes('similar_title')) return 'Titolo simile';
  if (reasons.includes('search_match')) return 'Corrispondenza della ricerca';
  return null;
}

export default function CalendarInviteIssueIndicator() {
  const [, navigate] = useLocation();
  const { openMartina } = useMartinaGlobal();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [linkingId, setLinkingId] = useState<number | null>(null);
  const [meetingSearch, setMeetingSearch] = useState('');
  const [selectedMeetingId, setSelectedMeetingId] = useState<number | null>(null);
  const deferredMeetingSearch = useDeferredValue(meetingSearch.trim());

  const { data, isFetching } = useQuery<CalendarInviteIssueResponse>({
    queryKey: CALENDAR_REVIEW_QUERY_KEY,
    queryFn: () => apiRequest('/api/email/promotion-buffer/calendar-invite-issues?limit=50'),
    refetchInterval: 10_000,
    staleTime: 5_000,
  });
  const issues = Array.isArray(data?.issues) ? data.issues : [];
  const reviewItems = Array.isArray(data?.review?.items) ? data.review.items : [];
  const pendingMeetings = reviewItems.filter((item) => item.entityType === 'meeting' && item.code === 'pending_confirmation');
  const attendancePendingMeetings = reviewItems.filter((item) => item.entityType === 'meeting' && item.code === 'attendance_pending');
  const reviewTotal = data?.review?.total ?? issues.length;

  useEffect(() => {
    const openReview = () => setOpen(true);
    window.addEventListener(CALENDAR_REVIEW_OPEN_EVENT, openReview);
    return () => window.removeEventListener(CALENDAR_REVIEW_OPEN_EVENT, openReview);
  }, []);

  const { data: candidateData, isFetching: candidatesLoading } = useQuery<ExistingMeetingCandidateResponse>({
    queryKey: ['/api/email/promotion-buffer/calendar-invite-issues/meeting-candidates', linkingId, deferredMeetingSearch],
    queryFn: () => apiRequest(
      `/api/email/promotion-buffer/calendar-invite-issues/${linkingId}/meeting-candidates${
        deferredMeetingSearch ? `?q=${encodeURIComponent(deferredMeetingSearch)}` : ''
      }`,
    ),
    enabled: linkingId !== null,
    staleTime: 10_000,
  });
  const candidates = Array.isArray(candidateData?.candidates) ? candidateData.candidates : [];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: CALENDAR_REVIEW_QUERY_KEY });

  const dismiss = useMutation({
    mutationFn: (messageId: number) =>
      apiRequest('POST', `/api/email/promotion-buffer/calendar-invite-issues/${messageId}/dismiss`, {}),
    onMutate: (messageId) => setBusyId(messageId),
    onSettled: () => {
      setBusyId(null);
      invalidate();
    },
  });

  const retry = useMutation({
    mutationFn: (messageId: number) =>
      apiRequest('POST', `/api/email/promotion-buffer/calendar-invite-issues/${messageId}/retry`, {}),
    onMutate: (messageId) => setBusyId(messageId),
    onSettled: () => {
      setBusyId(null);
      invalidate();
    },
  });

  const recoverRecentInvites = useMutation({
    mutationFn: () => apiRequest('POST', '/api/email/calendar-invites/backfill?giorni=14', {}),
    onSuccess: (result: any) => {
      const created = Number(result?.meetingCreati ?? 0);
      const existing = Number(result?.giaPresenti ?? 0);
      showSuccessNotification(
        created > 0
          ? `${created} ${created === 1 ? 'impegno recuperato' : 'impegni recuperati'} dalle email recenti.`
          : existing > 0
            ? 'Le email recenti erano già collegate al calendario.'
            : 'Controllo completato: nessun nuovo invito verificabile.',
      );
    },
    onSettled: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['/api/meetings'] });
    },
  });

  const linkExisting = useMutation({
    mutationFn: ({ messageId, meetingId }: { messageId: number; meetingId: number }) =>
      apiRequest(`/api/email/promotion-buffer/calendar-invite-issues/${messageId}/link-existing-meeting`, {
        method: 'POST',
        data: { meetingId },
        notifySuccess: false,
      }),
    onMutate: ({ messageId }) => setBusyId(messageId),
    onSuccess: (result, { messageId }) => {
      queryClient.setQueryData<CalendarInviteIssueResponse>(CALENDAR_REVIEW_QUERY_KEY, (current) => ({
        ...current,
        issues: current?.issues?.filter((issue) => issue.messageId !== messageId) ?? [],
      }));
      setLinkingId(null);
      setMeetingSearch('');
      setSelectedMeetingId(null);
      const meetingTitle = typeof result?.meetingTitle === 'string' && result.meetingTitle.trim()
        ? ` “${result.meetingTitle.trim()}”`
        : '';
      showSuccessNotification(`Collegamento salvato con l’evento${meetingTitle}. La segnalazione non verrà riproposta.`);
    },
    onSettled: () => {
      setBusyId(null);
      invalidate();
    },
  });

  const decidePendingMeeting = useMutation({
    mutationFn: ({ meetingId, decision }: { meetingId: number; decision: 'accepted' | 'declined' | 'dismissed' | 'deferred' }) =>
      apiRequest(`/api/meetings/${meetingId}/invite-response`, {
        method: 'POST',
        body: JSON.stringify({ decision, delivery: 'local_only', actionSurface: 'calendar_review' }),
      }),
    onMutate: ({ meetingId }) => setBusyId(-meetingId),
    onSuccess: (_result, { decision }) => {
      showSuccessNotification(decision === 'accepted'
        ? 'Appuntamento reale e confermato nel CRM. Nessuna risposta esterna è stata inviata.'
        : decision === 'dismissed'
          ? 'Errore di interpretazione registrato: il record non è più un appuntamento operativo.'
          : decision === 'deferred'
            ? 'Impegno riconosciuto come reale. La partecipazione resta da decidere nel CRM.'
            : 'Proposta reale rifiutata nel CRM. Nessuna risposta esterna è stata inviata.');
    },
    onSettled: () => {
      setBusyId(null);
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['/api/meetings'] });
      queryClient.invalidateQueries({ queryKey: ['/api/exai/rooms/focus'] });
    },
  });

  const toggleLinkPicker = (messageId: number) => {
    setLinkingId((current) => current === messageId ? null : messageId);
    setMeetingSearch('');
    setSelectedMeetingId(null);
  };

  const formatMeetingDate = (value: string) => {
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return 'Data da verificare';
    return new Intl.DateTimeFormat('it-IT', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const openEmail = (issue: CalendarInviteIssue) => {
    setOpen(false);
    navigate(issue.sourceHref.startsWith('/') ? issue.sourceHref : `/emails?emailId=${issue.messageId}`);
  };

  const askMartina = () => {
    const sources = reviewItems.map((item) => {
      const label = item.title?.trim() || item.id;
      const sourceHref = item.sourceHref.startsWith('http')
        ? item.sourceHref
        : `${window.location.origin}${item.sourceHref}`;
      const detail = item.entityType === 'meeting'
        ? `${item.startTime ? formatMeetingDate(item.startTime) : 'orario da verificare'}${item.organizerEmail ? ` · ${item.organizerEmail}` : ''}`
        : `${item.accountEmail ?? 'account non indicato'} · ${diagnosticLabel(item.diagnostic)}`;
      return `- [${label}](${sourceHref}) · ${CALENDAR_REVIEW_LABELS[item.code]} · ${detail}`;
    }).join('\n');
    setOpen(false);
    openMartina({
      context: calendarInviteIssueTerminalContext(issues),
      prompt: reviewTotal > 0
        ? `Ho ${reviewTotal} impegni di calendario da gestire. Spiegami per ciascuno cosa è verificabile e cosa manca; non accettare o rifiutare automaticamente. Le fonti sono cliccabili qui:\n${sources}\nProponi soltanto l'azione sicura successiva per ogni invito.`
        : 'Controlla se ci sono inviti calendario da riconciliare e mostrami soltanto evidenze verificate.',
    });
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="relative inline-flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        aria-label={reviewTotal > 0 ? `Apri ${reviewTotal} impegni di calendario da gestire` : 'Controlla gli inviti calendario'}
        title={reviewTotal > 0 ? `${reviewTotal} impegni di calendario da gestire` : 'Inviti calendario'}
      >
        <CalendarClock className="h-5 w-5" aria-hidden="true" />
        {reviewTotal > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-lg bg-red-600 px-1 text-xs font-semibold text-white">
            {reviewTotal > 99 ? '99+' : reviewTotal}
          </span>
        )}
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="flex max-w-none flex-col gap-0 overflow-hidden"
          style={{ width: 'min(100vw, 32rem)', maxWidth: 'none' }}
        >
          <SheetHeader className="border-b border-border pb-4 text-left">
            <SheetTitle>Impegni di calendario</SheetTitle>
            <SheetDescription>
              Un’unica coda con origine e stato verificabile. Le decisioni restano nel CRM;
              nessuna risposta esterna parte da questo pannello.
            </SheetDescription>
          </SheetHeader>

          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border py-3">
            <span className="text-xs text-muted-foreground">
              {reviewTotal === 0 ? 'Nessun impegno in coda' : `${reviewTotal} da gestire`}
              {isFetching ? ' · aggiornamento…' : ''}
            </span>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-11 gap-1"
                disabled={recoverRecentInvites.isPending}
                onClick={() => recoverRecentInvites.mutate()}
              >
                {recoverRecentInvites.isPending
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <RefreshCw className="h-3.5 w-3.5" />}
                Recupera inviti recenti
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-11 gap-1"
                disabled={reviewTotal === 0}
                onClick={askMartina}
              >
                <MessageSquare className="h-3.5 w-3.5" />
                Chiedi a Martina
              </Button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {reviewTotal === 0 ? (
              <p className="p-4 text-sm text-muted-foreground">
                Nessuna riconciliazione in sospeso.
              </p>
            ) : (
              <div className="divide-y divide-border">
                {[{
                  id: 'pending-calendar-review-title',
                  title: 'Proposte da verificare',
                  items: pendingMeetings,
                }, {
                  id: 'attendance-calendar-review-title',
                  title: 'Partecipazione da decidere',
                  items: attendancePendingMeetings,
                }].map((section) => section.items.length > 0 ? (
                  <section key={section.id} aria-labelledby={section.id}>
                    <h3 id={section.id} className="bg-muted/50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {section.title} · {section.items.length}
                    </h3>
                    <ul className="divide-y divide-border">
                      {section.items.map((item) => {
                        const meetingId = Number(item.meetingId);
                        const busy = busyId === -meetingId;
                        const entityKind = item.entityKind ?? 'appointment';
                        const entityLabel = CALENDAR_REVIEW_ENTITY_LABELS[entityKind];
                        const attendancePending = item.code === 'attendance_pending';
                        return (
                          <li key={item.id} className="space-y-3 p-4">
                            <div>
                              <div className="flex flex-wrap gap-2">
                                <span className="inline-flex rounded-full bg-sky-100 px-2 py-1 text-xs font-semibold text-sky-900">
                                  {entityLabel}
                                </span>
                                <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${attendancePending ? 'bg-violet-100 text-violet-900' : 'bg-amber-100 text-amber-900'}`}>
                                  {CALENDAR_REVIEW_LABELS[item.code]}
                                </span>
                              </div>
                              <p className="mt-2 text-sm font-medium leading-snug text-foreground">{item.title || `${entityLabel} #${meetingId}`}</p>
                              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                                {item.startTime ? formatMeetingDate(item.startTime) : 'Orario da verificare'}
                                {item.location ? ` · ${item.location}` : ''}
                                {item.organizerEmail ? ` · Da ${item.organizerEmail}` : ''}
                              </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Button type="button" size="sm" className="h-11 gap-1" disabled={busy} onClick={() => decidePendingMeeting.mutate({ meetingId, decision: 'accepted' })}>
                                <CheckCircle2 className="h-4 w-4" /> {entityKind === 'event' ? 'Partecipo' : 'Confermo'}
                              </Button>
                              <Button type="button" size="sm" variant="outline" className="h-11 gap-1" disabled={busy} onClick={() => decidePendingMeeting.mutate({ meetingId, decision: 'declined' })}>
                                <XCircle className="h-4 w-4" /> Non partecipo
                              </Button>
                              {!attendancePending ? (
                                <Button type="button" size="sm" variant="outline" className="h-11 gap-1" disabled={busy} onClick={() => decidePendingMeeting.mutate({ meetingId, decision: 'deferred' })}>
                                  <CalendarClock className="h-4 w-4" /> È reale: decido più avanti
                                </Button>
                              ) : null}
                              <Button type="button" size="sm" variant="outline" className="h-11 gap-1" disabled={busy} onClick={() => decidePendingMeeting.mutate({ meetingId, decision: 'dismissed' })}>
                                Errore d’interpretazione
                              </Button>
                              <Button type="button" size="sm" variant="outline" className="h-11 gap-1" onClick={() => { setOpen(false); navigate(`/meetings/${meetingId}`); }}>
                                <ExternalLink className="h-3.5 w-3.5" /> Dettagli e origine
                              </Button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                ) : null)}
                {issues.length > 0 ? <section aria-labelledby="unresolved-calendar-review-title">
                  <h3 id="unresolved-calendar-review-title" className="bg-muted/50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Errori da riconciliare · {issues.length}
                  </h3>
                  <ul className="divide-y divide-border">
                {issues.map((issue) => {
                  const title = issue.subject?.trim() || `Email #${issue.messageId}`;
                  const reason = diagnosticLabel(issue.diagnostic);
                  const reviewCode = reviewItems.find((item) => item.entityType === 'email_invite_issue' && item.messageId === issue.messageId)?.code;
                  const busy = busyId === issue.messageId;
                  return (
                    <li key={issue.messageId} className="space-y-3 p-4">
                      <div>
                        {reviewCode ? (
                          <span className="inline-flex rounded-full bg-rose-100 px-2 py-1 text-xs font-medium text-rose-900">
                            {CALENDAR_REVIEW_LABELS[reviewCode]}
                          </span>
                        ) : null}
                        <p className="text-sm font-medium leading-snug text-foreground">{title}</p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {reason}
                          {' · '}
                          {issue.accountEmail}
                          {' · '}
                          #{issue.messageId}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-11 gap-1"
                          disabled={busy}
                          onClick={() => openEmail(issue)}
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          Apri email
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          className="h-11"
                          disabled={busy || retry.isPending}
                          onClick={() => retry.mutate(issue.messageId)}
                        >
                          {busy && retry.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                          Riprova
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-11"
                          disabled={busy || dismiss.isPending}
                          onClick={() => dismiss.mutate(issue.messageId)}
                        >
                          {busy && dismiss.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                          Scarta
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-11 w-full gap-1 border-primary text-left sm:w-auto"
                          disabled={busy}
                          aria-expanded={linkingId === issue.messageId}
                          onClick={() => toggleLinkPicker(issue.messageId)}
                        >
                          <Link2 className="h-3.5 w-3.5" />
                          Fondi con evento esistente
                        </Button>
                      </div>

                      {linkingId === issue.messageId ? (
                        <div className="space-y-3 border border-border bg-muted/30 p-3" aria-label={`Fondi ${title} con evento esistente`}>
                          <div>
                            <p className="text-sm font-medium text-foreground">Scegli l’evento già presente</p>
                            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                              Il CRM collegherà questa segnalazione all’evento scelto. Non modifica il calendario e non invia risposte.
                            </p>
                          </div>
                          <div className="relative">
                            <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
                            <Input
                              value={meetingSearch}
                              onChange={(event) => {
                                setMeetingSearch(event.target.value);
                                setSelectedMeetingId(null);
                              }}
                              className="h-11 pl-9"
                              placeholder="Cerca per titolo o luogo"
                              aria-label="Cerca evento esistente"
                              autoFocus
                            />
                          </div>

                          <div className="max-h-72 space-y-2 overflow-y-auto" role="listbox" aria-label="Eventi esistenti">
                            {candidatesLoading ? (
                              <p className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" /> Ricerca eventi…
                              </p>
                            ) : candidates.length === 0 ? (
                              <p className="py-3 text-sm text-muted-foreground">
                                {meetingSearch.trim() ? 'Nessun evento trovato. Prova con altre parole.' : 'Nessuna corrispondenza evidente. Cerca per titolo o luogo.'}
                              </p>
                            ) : candidates.map((candidate) => {
                              const selected = selectedMeetingId === candidate.id;
                              const matchLabel = candidateMatchLabel(candidate.matchReasons);
                              return (
                                <button
                                  key={candidate.id}
                                  type="button"
                                  role="option"
                                  aria-selected={selected}
                                  className={`flex min-h-14 w-full items-start gap-3 border p-3 text-left transition-colors ${
                                    selected ? 'border-primary bg-accent text-accent-foreground' : 'border-border bg-background hover:bg-accent/60'
                                  }`}
                                  onClick={() => setSelectedMeetingId(candidate.id)}
                                >
                                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border border-current">
                                    {selected ? <Check className="h-3.5 w-3.5" /> : null}
                                  </span>
                                  <span className="min-w-0">
                                    <span className="block break-words text-sm font-medium leading-snug">{candidate.title}</span>
                                    <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                                      {formatMeetingDate(candidate.startTime)}
                                      {candidate.location ? ` · ${candidate.location}` : ''}
                                    </span>
                                    {matchLabel ? (
                                      <span className="mt-1 block text-xs text-primary">
                                        {matchLabel}
                                      </span>
                                    ) : null}
                                  </span>
                                </button>
                              );
                            })}
                          </div>

                          <div className="flex flex-col-reverse gap-2 border-t border-border pt-3 sm:flex-row sm:justify-end">
                            <Button
                              type="button"
                              variant="outline"
                              className="h-11"
                              disabled={busy}
                              onClick={() => toggleLinkPicker(issue.messageId)}
                            >
                              Annulla
                            </Button>
                            <Button
                              type="button"
                              className="h-11"
                              disabled={!selectedMeetingId || busy || linkExisting.isPending}
                              onClick={() => selectedMeetingId && linkExisting.mutate({
                                messageId: issue.messageId,
                                meetingId: selectedMeetingId,
                              })}
                            >
                              {busy && linkExisting.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                              Conferma collegamento
                            </Button>
                          </div>
                          {selectedMeetingId && !busy ? (
                            <p className="text-xs font-medium text-primary" role="status">
                              Evento selezionato. Premi “Conferma collegamento” per salvare la fusione.
                            </p>
                          ) : null}
                        </div>
                      ) : null}
                    </li>
                  );
                })}
                  </ul>
                </section> : null}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
